import React, { useEffect, useState } from 'react';
import { Box } from '../../elements';
import StepOne from './steps/StepOne';
import StepTwo from './steps/StepTwo';
import StepThree from './steps/StepThree';
import LastStep from './steps/LastStep';
import axios from 'axios';
import inputValidator from '../../../utils/inputValidator';
import { t } from '../../../../js/common/translations';

const Layout = ({
  submitPath,
  propertyId,
  propertyVisits,
  propertyAddress,
  memberSignedIn,
  currentUser,
  locale,
  apartmentsPath,
  homePath,
  recaptchaSiteKey
}) => {
  const steps = ['one', 'two', 'three', 'last'];
  const [step, setStep] = useState('one');
  const recaptchaRef = React.createRef();
  const [loading, setLoading] = useState(false);
  const [notAvailable, setNotAvailable] = useState([]);
  const [formErrorsText, setFormErrorsText] = useState([]);
  const [checkboxValue, setCheckboxValue] = useState(false);
  const validatorStepOne = [
    ['visitor_fullname', { validation: 'required', errorMessage: t('form_errors.empty_field') }],
    ['visitor_phone_number', { validation: 'required', errorMessage: t('form_errors.empty_field') }],
    ['visitor_phone_number', { validation: 'phone', errorMessage: t('form_errors.phone_invalid') }],
    ['visitor_email', { validation: 'required', errorMessage: t('form_errors.empty_field') }],
    ['visitor_email', { validation: 'email', errorMessage: t('form_errors.email_invalid') }]
  ];
  const validatorStepTwo = [
    ['date', { validation: 'required', errorMessage: t('visit_form.step_two.empty_date_error') }],
    ['time', { validation: 'required', errorMessage: t('visit_form.step_two.empty_time_error') }]
  ];
  const formValidations = step === 'one' ? validatorStepOne : validatorStepTwo;

  const [visitForm, setVisitForm] = useState({
    visitor_fullname: currentUser ? currentUser.fullname : '',
    visitor_phone_number: currentUser ? currentUser.phone_number : '',
    visitor_email: currentUser ? currentUser.email : '',
    date: '',
    time: '',
    extra_comments: ''
  });

  const getErrorMessage = (inputName) => {
    return formErrorsText.filter(el => el.field === inputName)[0]?.error;
  };

  useEffect(() => {
    if (visitForm.date) {
      const array = propertyVisits.filter((item) => item.scheduled_at.includes(visitForm.date));
      setNotAvailable(array);
    }

    if (memberSignedIn && currentUser.phone_number) {
      steps.shift();
      setStep(steps[0]);
    }
  }, [visitForm.date]);

  const handleCheckbox = () => {
    setCheckboxValue(!checkboxValue);
  };

  const handleChange = (inputName) => ({ target: { value } }) => {
    if (inputName === 'date' && visitForm.time !== '') {
      setVisitForm(state => ({
        ...state,
        time: ''
      }));
    }
    const validate = {
      [inputName]: value
    };
    const errors = inputValidator(validate, formValidations.filter(el => el[0] === inputName));
    setFormErrorsText(errors);
    setVisitForm(state => ({
      ...state,
      [inputName]: value
    }));
  };

  const visitFormData = () => {
    const formData = new FormData();
    const scheduledAt = `${visitForm.date} ${visitForm.time}`;

    formData.append('visit_form[visitor_fullname]', visitForm.visitor_fullname);
    formData.append('visit_form[visitor_phone_number]', visitForm.visitor_phone_number);
    formData.append('visit_form[visitor_email]', visitForm.visitor_email);
    formData.append('visit_form[scheduled_at]', scheduledAt);
    formData.append('visit_form[extra_comments]', visitForm.extra_comments);
    formData.append('visit_form[status]', 'Approved');
    formData.append('visit_form[property_internal_id', propertyId);

    return formData;
  };

  const handleSubmit = async() => {
    const form = visitFormData();
    const recaptchaValue = !memberSignedIn ? recaptchaRef.current.getValue() : true;
    let stepThreeErrors = [];

    if (!recaptchaValue) {
      stepThreeErrors = [...stepThreeErrors, { field: 'recaptcha', error: t('visit_form.step_three.recaptcha_error') }];
    }

    if (!checkboxValue) {
      stepThreeErrors = [...stepThreeErrors, { field: 'agreement', error: t('form_errors.agreement_checkbox_not_checked') }];
    }

    if (recaptchaValue && checkboxValue) {
      setFormErrorsText([]);
      setLoading(true);

      try {
        await axios.post(submitPath, form);
        setStep('last');
      } catch (e) {
        console.error(e);
      }
    } else {
      setFormErrorsText(stepThreeErrors);
    }
  };

  const handleSetStep = ({ step, back }) => {
    const errors = inputValidator(visitForm, formValidations);

    if (back) {
      setStep(step);
      return;
    }

    if (errors.length) {
      setFormErrorsText(errors);
    } else {
      setStep(step);
    }
  };

  return (
    <Box sx={{ width: '100%', height: { xs: 'auto', md: '100vh' }, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {(steps.includes(step) && (
        {
          one: (
            <StepOne
              handleSetStep={handleSetStep}
              setVisitForm={handleChange}
              visitForm={visitForm}
              backButton={''}
              getErrorMessage={getErrorMessage}
            />
          ),
          two: (
            <StepTwo
              handleSetStep={handleSetStep}
              notAvailable={notAvailable}
              handleChange={handleChange}
              setVisitForm={setVisitForm}
              visitForm={visitForm}
              memberSignedIn={memberSignedIn}
              getErrorMessage={getErrorMessage}
              setFormErrorsText={setFormErrorsText}
            />
          ),
          three: (
            <StepThree
              handleSetStep={handleSetStep}
              handleSubmit={handleSubmit}
              loading={loading}
              memberSignedIn={memberSignedIn}
              visitForm={visitForm}
              propertyAddress={propertyAddress}
              locale={locale}
              siteKey={recaptchaSiteKey}
              getErrorMessage={getErrorMessage}
              recaptchaRef={recaptchaRef}
              handleChange={handleChange}
              handleCheckbox={handleCheckbox}
              checkboxValue={checkboxValue}
            />
          ),
          last: (
            <LastStep
              visitForm={visitForm}
              propertyAddress={propertyAddress}
              apartmentsPath={apartmentsPath}
              homePath={homePath}
              locale={locale}
            />
          )
        }[step]
      ))}
    </Box>
  );
};

export default Layout;
